import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Slide,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';

const StyledDialog = styled(Dialog)(({ theme, size }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    margin: theme.spacing(2),
    width: '100%',

    // Size variants
    ...(size === 'small' && {
      maxWidth: '400px',
    }),
    ...(size === 'medium' && {
      maxWidth: '600px',
    }),
    ...(size === 'large' && {
      maxWidth: '900px',
    }),
    ...(size === 'xl' && {
      maxWidth: '1200px',
    }),
    ...(size === 'fullscreen' && {
      maxWidth: 'none',
      maxHeight: 'none',
      margin: 0,
      borderRadius: 0,
      height: '100vh',
      width: '100vw',
    }),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: '64px',
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
  '&:first-of-type': {
    paddingTop: theme.spacing(3),
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
}));

// Transition components
const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const FadeTransition = React.forwardRef(function Transition(props, ref) {
  return <Fade ref={ref} {...props} />;
});

/**
 * Reusable Modal component with consistent styling and features
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Modal open state
 * @param {function} props.onClose - Close handler
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.children - Modal content
 * @param {React.ReactNode} props.actions - Modal actions (buttons)
 * @param {string} props.size - Modal size variant
 * @param {boolean} props.fullScreen - Full screen on mobile
 * @param {boolean} props.disableCloseButton - Hide close button
 * @param {boolean} props.disableBackdropClick - Disable backdrop click to close
 * @param {boolean} props.disableEscapeKeyDown - Disable escape key to close
 * @param {string} props.transition - Transition animation
 * @param {number} props.maxWidth - Custom max width
 * @param {boolean} props.scroll - Enable content scrolling
 */
const Modal = ({
  open,
  onClose,
  title,
  children,
  actions,
  size = 'medium',
  fullScreen = false,
  disableCloseButton = false,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  transition = 'slide',
  maxWidth,
  scroll = 'paper',
  ...otherProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const shouldBeFullScreen = fullScreen || (isMobile && size !== 'small');

  const TransitionComponent = transition === 'fade' ? FadeTransition : SlideTransition;

  const handleClose = (event, reason) => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    if (disableEscapeKeyDown && reason === 'escapeKeyDown') {
      return;
    }
    if (onClose) {
      onClose(event, reason);
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      size={size}
      fullScreen={shouldBeFullScreen}
      TransitionComponent={TransitionComponent}
      scroll={scroll}
      disableEscapeKeyDown={disableEscapeKeyDown}
      sx={{
        ...(maxWidth && {
          '& .MuiDialog-paper': {
            maxWidth: maxWidth,
          },
        }),
      }}
      {...otherProps}
    >
      {title && (
        <StyledDialogTitle>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {!disableCloseButton && (
            <IconButton
              aria-label="close"
              onClick={(e) => handleClose(e, 'closeButton')}
              sx={{
                color: theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </StyledDialogTitle>
      )}

      <StyledDialogContent>{children}</StyledDialogContent>

      {actions && <StyledDialogActions>{actions}</StyledDialogActions>}
    </StyledDialog>
  );
};

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  title: PropTypes.string,
  children: PropTypes.node,
  actions: PropTypes.node,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'xl', 'fullscreen']),
  fullScreen: PropTypes.bool,
  disableCloseButton: PropTypes.bool,
  disableBackdropClick: PropTypes.bool,
  disableEscapeKeyDown: PropTypes.bool,
  transition: PropTypes.oneOf(['slide', 'fade']),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scroll: PropTypes.oneOf(['body', 'paper']),
};

export default Modal;
